/* ================================================================
   jaiio-editorial.js — comportements de la coquille éditoriale (T080)

   <jaiio-toc>        rail « Sommaire » : l'entrée du titre en cours de
                      lecture porte `aria-current` (le point corail).
   <jaiio-copy-link>  « Copier le lien » : copie l'URL canonique reçue en
                      attribut, et le dit dans une zone `aria-live`.
   <jaiio-size-mark>  guide des tailles : marque la colonne de la cliente.

   Les libellés viennent de Liquid (attributs `data-*`) : aucun texte
   utilisateur n'est écrit ici.
   ================================================================ */

// Bloc : les classes y restent locales. Deux sections de la même page
// (page éditoriale et FAQ) chargent ce fichier ; sans lui, la seconde
// exécution échoue sur « Identifier has already been declared ».
{
class JaiioToc extends HTMLElement {
  connectedCallback() {
    this.links = [...this.querySelectorAll('a[href^="#"]')];
    this.targets = this.links
      .map((link) => document.getElementById(decodeURIComponent(link.hash.slice(1))))
      .filter(Boolean);
    if (!this.targets.length) return;

    // L'entrée « en cours » est la cible la plus basse dont le haut a passé la
    // ligne de lecture ; avant toutes, la première ; en bas de page, la plus
    // basse, qu'une section courte ne laisserait jamais atteindre la ligne.
    // Calcul à la position, et non par IntersectionObserver : avec des sections
    // entières pour cibles (Mon profil, T090), la précédente déborde encore dans
    // la bande et l'observateur la retenait ; il ne revenait pas non plus à la
    // première en remontant tout en haut. La position compte, pas l'ordre du
    // sommaire, et une cible masquée (réponse de FAQ fermée) est ignorée.
    this.update = () => {
      this.frame = null;
      // Sommaire non affiché (rail masqué sur mobile, accordéon fermé) : rien à marquer.
      if (!this.getClientRects().length) return;
      if (this.pinned) {
        this.setCurrent(this.pinned);
        return;
      }
      // Ligne de lecture : là où un lien d'ancre dépose la cible (son
      // scroll-margin, sous l'en-tête collé), sinon le tiers haut de la fenêtre.
      const margin = parseFloat(getComputedStyle(this.targets[0]).scrollMarginBlockStart) || 0;
      const line = margin > 0 ? margin + 8 : window.innerHeight / 3;
      const atBottom = window.scrollY > 0 && window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
      let current = null;
      let currentTop = -Infinity;
      let first = null;
      let firstTop = Infinity;
      this.targets.forEach((target) => {
        if (!target.getClientRects().length) return;
        const top = target.getBoundingClientRect().top;
        if (top < firstTop) {
          first = target;
          firstTop = top;
        }
        if ((atBottom || top <= line) && top > currentTop) {
          current = target;
          currentTop = top;
        }
      });
      current = current || first;
      if (current) this.setCurrent(current.id);
    };
    this.onScroll = () => {
      if (!this.frame) this.frame = requestAnimationFrame(this.update);
    };

    // Clic dans le sommaire : l'entrée choisie reste marquée pendant le saut,
    // même si la page ne peut pas descendre assez pour amener la cible sur la
    // ligne ; le prochain geste de la lectrice (molette, toucher, clavier) rend
    // la main au calcul.
    this.onPick = (event) => {
      const link = event.target.closest('a[href^="#"]');
      if (!link) return;
      this.pinned = decodeURIComponent(link.hash.slice(1));
      this.setCurrent(this.pinned);
    };
    this.onRelease = () => {
      if (!this.pinned) return;
      this.pinned = null;
      this.onScroll();
    };
    this.addEventListener('click', this.onPick);
    // Arrivée par une ancre (« Mes informations bancaires » → Mon profil#pf-iban) : même règle.
    const hash = decodeURIComponent(window.location.hash.slice(1));
    if (hash && this.targets.some((target) => target.id === hash)) this.pinned = hash;
    ['wheel', 'touchstart', 'keydown'].forEach((type) => window.addEventListener(type, this.onRelease, { passive: true }));

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    // La mise en page bouge aussi sans défilement : accordéons, images, chargement.
    document.addEventListener('toggle', this.onScroll, true);
    window.addEventListener('load', this.onScroll, { once: true });
    this.update();
  }

  disconnectedCallback() {
    if (!this.onScroll) return;
    this.removeEventListener('click', this.onPick);
    ['wheel', 'touchstart', 'keydown'].forEach((type) => window.removeEventListener(type, this.onRelease));
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    document.removeEventListener('toggle', this.onScroll, true);
    window.removeEventListener('load', this.onScroll);
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  setCurrent(id) {
    // Rien à réécrire si l'entrée n'a pas changé : le calcul tourne à chaque image.
    if (id === this.currentId) return;
    this.currentId = id;
    this.links.forEach((link) => {
      if (decodeURIComponent(link.hash.slice(1)) === id) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
  }
}

class JaiioCopyLink extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.status = this.querySelector('[aria-live]');
    if (!this.button) return;
    this.onClick = this.copy.bind(this);
    this.button.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    if (this.button) this.button.removeEventListener('click', this.onClick);
  }

  async copy() {
    try {
      if (!navigator.clipboard) throw new Error('clipboard indisponible');
      await navigator.clipboard.writeText(this.dataset.url);
      this.say(this.dataset.copied);
    } catch (error) {
      // Contexte non sécurisé ou permission refusée : on donne l'adresse
      // à copier à la main plutôt que de laisser croire que c'est fait.
      this.say(`${this.dataset.failed} ${this.dataset.url}`);
    }
  }

  say(message) {
    if (!this.status) return;
    this.status.textContent = message;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => { this.status.textContent = ''; }, 4000);
  }
}

// Guide des tailles : marque la colonne du tableau dont l'en-tête porte une
// taille enregistrée par la cliente (« 38 », « M », « 39 »). En-tête « S / M » :
// chaque morceau compte. Aucun rapprochement entre systèmes de tailles — on ne
// marque que ce que le tableau dit lui-même (voir main-page-guide-tailles).
class JaiioSizeMark extends HTMLElement {
  connectedCallback() {
    let sizes = [];
    try { sizes = JSON.parse(this.dataset.sizes || '[]'); } catch (error) { return; }
    if (!Array.isArray(sizes) || !sizes.length) return;
    const norm = (value) => String(value).trim().toUpperCase().replace(/^(FR|EU)\s*/, '');
    const wanted = new Set(sizes.map(norm));
    let marked = false;
    this.querySelectorAll('table').forEach((table) => {
      const heads = [...table.querySelectorAll('thead th')];
      heads.forEach((th, column) => {
        if (column === 0) return;
        if (!th.textContent.split(/[\/,]/).map(norm).some((token) => wanted.has(token))) return;
        marked = true;
        th.classList.add('is-me');
        table.querySelectorAll('tbody tr').forEach((row) => {
          if (row.children[column]) row.children[column].classList.add('is-me');
        });
      });
    });
    const note = this.querySelector('[data-size-note]');
    if (marked && note) note.hidden = false;
  }
}

// « Lancer le chat » (page Contact) : le bouton est un lien vers le centre
// d'aide ; quand la messagerie Zendesk est chargée, il l'ouvre à la place.
// Un seul écouteur, même si le fichier est chargé deux fois.
if (!window.jaiioChatBound) {
  window.jaiioChatBound = true;
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-jaiio-chat]');
    if (!link || typeof window.zE !== 'function') return;
    event.preventDefault();
    try {
      window.zE('messenger', 'open');
    } catch (error) {
      window.zE('webWidget', 'open');
    }
  });
}

if (!customElements.get('jaiio-toc')) customElements.define('jaiio-toc', JaiioToc);
if (!customElements.get('jaiio-copy-link')) customElements.define('jaiio-copy-link', JaiioCopyLink);
if (!customElements.get('jaiio-size-mark')) customElements.define('jaiio-size-mark', JaiioSizeMark);
}
